import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2023 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */
import { Expression } from "abreact/expression/Expression";
import { FormulaElement } from "abreact/expression/formula/framework/FormulaElement";
import { FormulaElementType } from "abreact/expression/formula/framework/FormulaElementType";
import { FormulaState } from "abreact/expression/formula/framework/FormulaState";
import { BinarySubTree, FormulaToExpressionTreeUtils } from "abreact/expression/formula/framework/FormulaToExpressionTreeUtils";
import { GuidedExpressionEditorUtils } from "abreact/expression/ui/editor/impl/utils/GuidedExpressionEditorUtils";
import { ExpExpressionNodeNodeTypeEnum } from "abweb/generated/abwebinternal/api/dto/ExpExpressionNode";
import { Assert } from "abweb/utils/Assert";
import { ClassUtils } from "abweb/utils/ClassUtils";
export class FormulaElementModelAdaptor {
  constructor(formulaElementModel, formulaComponentFactory, summaryGenerator) {
    _defineProperty(this, "_formulaElementModel", void 0);
    _defineProperty(this, "_formulaComponentFactory", void 0);
    _defineProperty(this, "_summaryGenerator", void 0);
    this._formulaElementModel = formulaElementModel;
    this._formulaComponentFactory = formulaComponentFactory;
    this._summaryGenerator = summaryGenerator;
  }
  createOperandValue() {
    try {
      let util = new FormulaToExpressionTreeUtils(this._formulaElementModel, this._formulaComponentFactory.expressionDomainModel, this._summaryGenerator);
      let tree = util.createTree();
      let operandValue = ClassUtils.isInstanceOf(tree, BinarySubTree) ? tree.toOperatorOperandValue() : tree;
      operandValue = this.addAdditionalData(operandValue);
      return operandValue;
    } catch (e) {
      console.log(e);
    }
  }
  addAdditionalData(operandValue) {
    const additionalData = {
      type: FormulaElementModelAdaptor.ADDITIONAL_DATA_FORMULA_TEMPLATE_TYPE,
      values: {
        template: JSON.stringify(this.generateFormulaTemplate(), null, 2)
      }
    };
    if (!this._formulaElementModel.isComplete()) {
      additionalData.values[Expression.Domain.Constants.ADDITIONAL_DATA_COMPLETE_OVERRIDE] = "false";
    }
    return operandValue.withExpAdditionalData(additionalData);
  }
  static isFormulaElementNonValueType(value) {
    switch (value) {
      case FormulaElementType.OPERATOR_ADD:
      case FormulaElementType.OPERATOR_SUBTRACT:
      case FormulaElementType.OPERATOR_DIVIDE:
      case FormulaElementType.OPERATOR_MULTIPLY:
      case FormulaElementType.PAREN_LEFT:
      case FormulaElementType.PAREN_RIGHT:
        return true;
      default:
        return false;
    }
  }
  generateFormulaTemplate() {
    let template = this._formulaElementModel.getNoPlaceHolderElements().map(element => {
      if (element.type === FormulaElementType.VALUE_NODE) {
        return element.id;
      } else {
        return element.type;
      }
    });
    return template;
  }
  static createStateFromOperandValue(operandValue, generateId) {
    let formulaState = new FormulaState([], {}, generateId);
    if (operandValue.expExpressionNodeNodeType === ExpExpressionNodeNodeTypeEnum.OPERATOR) {
      const {
        expOperator
      } = operandValue;
      Assert.assert(expOperator.roleID === Expression.Domain.Constants.ROLE_FORMULA, `cannot load a non-formula operand value into a formula editor operatorID=${expOperator.id} operatorRole=${expOperator.roleID} nodeId=${operandValue.id}`);
    }
    this.processFormulaOperandValue(operandValue, formulaState);
    return formulaState;
  }
  static processFormulaOperandValue(op, state) {
    const templateString = op.expAdditionalData?.values.template;
    Assert.assert(templateString != null, "template must be provided within additional data of numeric formula node");
    const template = JSON.parse(templateString);
    this.populateOperandValues(op, state);
    template.forEach(element => {
      if (!FormulaElementModelAdaptor.isFormulaElementNonValueType(element)) {
        state.elements.push(new FormulaElement(element, FormulaElementType.VALUE_NODE));
      } else {
        FormulaElementModelAdaptor.addElementToStateFromString(state, element);
      }
    });
  }
  static addElementToStateFromString(state, element) {
    switch (element) {
      case FormulaElementType.OPERATOR_ADD:
        state.elements.push(new FormulaElement(state.generateId(), FormulaElementType.OPERATOR_ADD));
        break;
      case FormulaElementType.OPERATOR_SUBTRACT:
        state.elements.push(new FormulaElement(state.generateId(), FormulaElementType.OPERATOR_SUBTRACT));
        break;
      case FormulaElementType.OPERATOR_DIVIDE:
        state.elements.push(new FormulaElement(state.generateId(), FormulaElementType.OPERATOR_DIVIDE));
        break;
      case FormulaElementType.OPERATOR_MULTIPLY:
        state.elements.push(new FormulaElement(state.generateId(), FormulaElementType.OPERATOR_MULTIPLY));
        break;
      case FormulaElementType.PAREN_LEFT:
        state.elements.push(new FormulaElement(state.generateId(), FormulaElementType.PAREN_LEFT));
        break;
      case FormulaElementType.PAREN_RIGHT:
        state.elements.push(new FormulaElement(state.generateId(), FormulaElementType.PAREN_RIGHT));
        break;
      default:
        throw new Error("Unrecognized element in formula template: " + element);
    }
  }
  static getAllValueNodeOperandValues(operandValue) {
    const operandValues = new Set();
    const allValueNodeOperandValues = [];
    GuidedExpressionEditorUtils.collectOperandValues(operandValue, operandValues);
    operandValues.forEach(ov => {
      if (ov.expExpressionNodeNodeType === ExpExpressionNodeNodeTypeEnum.OPERATOR) {
        let operatorNode = ov;
        if (operatorNode.expOperator.roleID !== Expression.Domain.Constants.ROLE_FORMULA) {
          allValueNodeOperandValues.push(ov);
        }
      } else {
        allValueNodeOperandValues.push(ov);
      }
    });
    return allValueNodeOperandValues;
  }
  static populateOperandValues(operandValue, state) {
    FormulaElementModelAdaptor.getAllValueNodeOperandValues(operandValue).forEach(ov => {
      state.setOperandValueForElementId(ov.id, ov);
    });
  }
}
_defineProperty(FormulaElementModelAdaptor, "ADDITIONAL_DATA_FORMULA_TEMPLATE_TYPE", "formula-template");
//# sourceMappingURL=FormulaElementModelAdaptor.js.map
